/**
 * Serviço para buscar credenciais SMB para acesso à rede
 * Usado principalmente pelo fluxo SaveTemp
 */

import { api } from "../lib/api";
import { logger } from "../lib/terminalLogger";

export interface SmbCredential {
	id: number;
	name: string;
	username: string;
	password_enc: string;
	domain: string | null;
	active: boolean;
	created_at: string;
	updated_at: string;
	created_by: number;
	updated_by: number | null;
}

export interface SmbCredentialFormatted {
	username: string;
	password_enc: string;
	domain: string | null;
}

class SmbCredentialsService {
	private cache: Map<number, SmbCredential> = new Map();
	private cacheExpiry: Map<number, number> = new Map();
	private readonly CACHE_DURATION = 5 * 60 * 1000; // 5 minutos

	/**
	 * Busca credencial SMB por ID
	 * @param credentialId ID da credencial (ex: 3 para svc_o_vendas)
	 * @returns Credencial SMB
	 */
	async getCredential(credentialId: number): Promise<SmbCredential> {
		// Verificar cache
		const cached = this.cache.get(credentialId);
		const expiry = this.cacheExpiry.get(credentialId);

		if (cached && expiry && Date.now() < expiry) {
			return cached;
		}

		try {
			const response = await api(`/cdr/smb-credential/${credentialId}`);
			const credential = response.data || response;

			// Validar resposta
			if (!credential || !credential.username) {
				throw new Error("Credencial inválida recebida da API");
			}

			// Armazenar no cache
			this.cache.set(credentialId, credential);
			this.cacheExpiry.set(credentialId, Date.now() + this.CACHE_DURATION);

			return credential;
		} catch (error) {
			logger.error("Erro ao buscar credencial SMB:", error);

			// ❌ REMOVIDO: Fallback hardcoded (viola regra de negócio)
			// Rules não podem ter fallback - deve notificar erro ao usuário
			throw error; // ← Propagar erro para tratamento no chamador
		}
	}

	/**
	 * Formata credencial para uso no payload de salvamento
	 * @param credentialId ID da credencial
	 * @returns Objeto formatado para o payload
	 */
	async getFormattedCredential(
		credentialId: number,
	): Promise<SmbCredentialFormatted> {
		const credential = await this.getCredential(credentialId);

		return {
			username: credential.username,
			password_enc: credential.password_enc,
			domain: credential.domain,
		};
	}

	/**
	 * ❌ REMOVIDO: getFallbackCredential()
	 * Fallback hardcoded viola regra de negócio:
	 * "rules não podem ter fallback, caso a rota não retorne deve notificar erro"
	 */

	/**
	 * Limpa o cache de credenciais
	 */
	clearCache(): void {
		this.cache.clear();
		this.cacheExpiry.clear();
	}

	/**
	 * Pré-carrega credencial (útil para evitar latência na primeira vez)
	 * @param credentialId ID da credencial
	 */
	async preload(credentialId: number): Promise<void> {
		try {
			await this.getCredential(credentialId);
		} catch (error) {
			logger.error("Erro ao pré-carregar credencial:", error);
		}
	}
}

// Export singleton
export const smbCredentialsService = new SmbCredentialsService();
